import React, { useEffect, useRef, useState } from "react";
import { Box, Paper, Typography, Button, CircularProgress, CssBaseline } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useNavigate, useSearchParams } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const lightTheme = createTheme({ palette: { mode: "light" } });

// Opened from the activation email: /activate?token=…
const ActivateAccountPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [state, setState] = useState({ status: "working" });
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return; // StrictMode runs effects twice; the link is used once
    started.current = true;
    const token = params.get("token");
    if (!token) {
      setState({ status: "error", message: "This activation link is not complete. Open it from the email again." });
      return;
    }
    fetch("/api/auth/activation/confirm", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    })
      .then(async (res) => {
        const d = await res.json().catch(() => ({}));
        if (!res.ok || d.success === false) throw new Error(d.message || "This activation link did not work.");
        setState({ status: "done", username: d.username });
      })
      .catch((e) => setState({ status: "error", message: e.message }));
  }, [params]);

  return (
    <ThemeProvider theme={lightTheme}>
      <CssBaseline />
      <Box sx={{
        minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", px: 2,
        background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 55%, #1d4ed8 100%)",
      }}>
        <Paper elevation={24} sx={{ borderRadius: "20px", p: { xs: 3, sm: 4 }, width: "100%", maxWidth: 440, textAlign: "center" }}>
          {state.status === "working" && (
            <>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography>Activating your account…</Typography>
            </>
          )}
          {state.status === "done" && (
            <>
              <CheckCircleIcon sx={{ fontSize: 56, color: "#16a34a", mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Account activated</Typography>
              <Typography sx={{ color: "text.secondary", mb: 3 }}>
                {state.username ? <>You can now sign in as <b>{state.username}</b>.</> : "You can now sign in."}
              </Typography>
              <Button fullWidth variant="contained" onClick={() => navigate("/login")}
                sx={{ py: 1.2, borderRadius: "10px", textTransform: "none", fontWeight: 700 }}>
                Sign in
              </Button>
            </>
          )}
          {state.status === "error" && (
            <>
              <ErrorOutlineIcon sx={{ fontSize: 56, color: "#dc2626", mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Could not activate</Typography>
              <Typography sx={{ color: "text.secondary", mb: 3 }}>{state.message}</Typography>
              <Button fullWidth variant="outlined" onClick={() => navigate("/login")}
                sx={{ py: 1.2, borderRadius: "10px", textTransform: "none", fontWeight: 700 }}>
                Go to sign in
              </Button>
            </>
          )}
        </Paper>
      </Box>
    </ThemeProvider>
  );
};

export default ActivateAccountPage;
